import HighchartsReactComponent from '@components/_elements/HighChart';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import React from 'react';
import ChartLoadingOverlay from './ChartLoadingOverlay';
import ChartStatistics from './ChartStatistics';
import { chartContentStyles } from './styles';

import type { ChartContentProps, HighchartsReactProps } from './types';

const HighchartsReact =
  HighchartsReactComponent as React.FC<HighchartsReactProps>;

const ChartContent: React.FC<ChartContentProps> = ({
  isLoading,
  isFetching,
  error,
  data,
  statistics,
  currentTemperature,
  chartOptions,
  onRefresh,
}) => {
  // Show full loading screen only on initial load (no data yet)
  if (isLoading || (isFetching && !data)) {
    return (
      <Box sx={chartContentStyles.loadingContainer}>
        <CircularProgress />
        <Typography variant='body1' color='text.secondary'>
          Loading historical data...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={chartContentStyles.errorContainer}>
        <Typography variant='body1' color='error'>
          Failed to load historical data.
        </Typography>
        <Button
          variant='contained'
          onClick={onRefresh}
          sx={chartContentStyles.retryButton}
        >
          Retry
        </Button>
      </Box>
    );
  }

  if ((!data || data.length === 0) && (!isLoading || !isFetching)) {
    return (
      <Box sx={chartContentStyles.noDataContainer}>
        <Typography variant='body1' color='text.secondary'>
          No historical data available for this device.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {statistics && (
        <ChartStatistics
          statistics={statistics}
          currentTemperature={currentTemperature}
        />
      )}
      <Box sx={chartContentStyles.chartContainer}>
        <HighchartsReact
          options={chartOptions}
          constructorType='chart'
          immutable={false}
          allowChartUpdate={true}
          updateArgs={[true, false, false]}
        />
        <ChartLoadingOverlay isFetching={isFetching} />
      </Box>
    </>
  );
};

export default React.memo(ChartContent);
